import { useEffect, useState } from "react";
import { X } from "lucide-react";
import AutocompleteInput from "./AutocompleteInput";
import DatePickerField from "./DatePickerField";
import { api } from "../lib/api";

const CATEGORIES = ["Publicidad", "Viajes", "Gasolina", "Oficina", "Comida", "Nómina", "Internet", "Software", "Renta", "Servicios", "Otras"];
const PAYMENT_METHODS = ["Transferencia", "Depósito", "Efectivo", "Crypto", "Otro"];

const empty = {
  merchant: "",
  amount: "",
  purchase_date: new Date().toISOString().slice(0, 10),
  category: "Software",
  payment_method: "Transferencia",
  supplier: "",
  note: "",
  has_invoice: false,
};

/**
 * Reusable Expense form modal — matches ExpensesPage exactly.
 * Props identical to IncomeFormModal but for expenses.
 */
export default function ExpenseFormModal({ open, onClose, onSaved, editingExpense = null, projectId = null, testidScope = "expense" }) {
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    setError("");
    if (editingExpense) {
      setForm({
        merchant: editingExpense.merchant || "",
        amount: String(editingExpense.amount ?? ""),
        purchase_date: (editingExpense.purchase_date || editingExpense.created_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
        category: editingExpense.category || "Otras",
        payment_method: editingExpense.payment_method || "Transferencia",
        supplier: editingExpense.supplier || "",
        note: editingExpense.note || "",
        has_invoice: Boolean(editingExpense.has_invoice),
      });
    } else {
      setForm(empty);
    }
  }, [open, editingExpense]);

  useEffect(() => {
    if (!open) return;
    api.get("/workspace/suppliers", { withCredentials: true })
      .then((r) => setSupplierOptions((r.data || []).map((s) => s.name).filter(Boolean)))
      .catch(() => setSupplierOptions([]));
  }, [open]);

  if (!open) return null;

  const createSupplier = async (name) => {
    try {
      const r = await api.post("/workspace/suppliers", { name }, { withCredentials: true });
      setSupplierOptions((cur) => [...cur, r.data.name]);
      return r.data.name;
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos crear el proveedor.");
      return null;
    }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setError("");
    const amount = Number(form.amount);
    if (!form.merchant.trim() || !amount || amount <= 0) {
      setError("Concepto y monto son obligatorios.");
      return;
    }
    setSaving(true);
    try {
      let response;
      if (editingExpense) {
        response = await api.put(`/expenses/${editingExpense.id}`, {
          merchant: form.merchant,
          amount,
          purchase_date: form.purchase_date,
          category: form.category,
          payment_method: form.payment_method,
          supplier: form.supplier,
          has_invoice: form.has_invoice,
          note: form.note,
        }, { withCredentials: true });
      } else if (projectId) {
        response = await api.post(`/projects/${projectId}/expenses`, {
          merchant: form.merchant,
          amount,
          date: form.purchase_date,
          category: form.category,
          payment_method: form.payment_method,
          supplier: form.supplier,
          has_invoice: form.has_invoice,
          note: form.note,
        }, { withCredentials: true });
      } else {
        const body = new FormData(ev.currentTarget);
        body.set("type", "expense");
        body.set("note", form.merchant);
        body.set("details", form.note);
        body.set("amount", String(amount));
        body.set("date", form.purchase_date);
        body.set("category", form.category);
        body.set("payment_method", form.payment_method);
        body.set("supplier", form.supplier);
        body.set("has_invoice", form.has_invoice ? "true" : "false");
        response = await api.post("/smart-input/manual-file", body, { withCredentials: true });
      }
      onSaved && onSaved(response.data);
      onClose && onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos guardar el egreso.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="income-modal-overlay" data-testid={`${testidScope}-modal`}>
      <form className="income-modal income-modal-form" onSubmit={submit} data-testid={`${testidScope}-form`}>
        <button type="button" className="modal-close" data-testid={`${testidScope}-modal-close`} onClick={onClose} aria-label="Cerrar"><X /></button>
        <h2>{editingExpense ? "Editar Egreso" : "Añadir Egreso"}</h2>
        <label>Concepto
          <input data-testid={`${testidScope}-merchant-input`} value={form.merchant} onChange={(e) => setForm({ ...form, merchant: e.target.value })} required />
        </label>
        <label>Monto
          <input data-testid={`${testidScope}-amount-input`} type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
        </label>
        <label>Fecha
          <DatePickerField value={form.purchase_date} onChange={(v) => setForm({ ...form, purchase_date: v })} testId={`${testidScope}-date-input`} />
        </label>
        <label>Categoría
          <select data-testid={`${testidScope}-category-select`} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>Método de pago
          <select data-testid={`${testidScope}-payment-select`} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
            {PAYMENT_METHODS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </label>
        <label>Proveedor / Empresa (opcional)
          <AutocompleteInput value={form.supplier} onChange={(v) => setForm({ ...form, supplier: v })} options={supplierOptions} onCreate={createSupplier} createLabel="Crear proveedor" placeholder="Ej: Starbucks, Renta oficina..." testId={`${testidScope}-supplier-input`} />
        </label>
        <label>Nota (opcional)
          <textarea data-testid={`${testidScope}-note-input`} rows="2" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </label>
        <label className="expenses-inline-check">
          <input data-testid={`${testidScope}-has-invoice-checkbox`} type="checkbox" checked={form.has_invoice} onChange={(e) => setForm({ ...form, has_invoice: e.target.checked })} />
          <span>Tiene factura (CFDI)</span>
        </label>
        {!editingExpense && !projectId && (
          <label>Adjuntar archivo (opcional)
            <input data-testid={`${testidScope}-file-input`} name="file" type="file" accept="application/pdf,image/*" />
          </label>
        )}
        {error && <p className="billing-error" data-testid={`${testidScope}-form-error`}>{error}</p>}
        <button type="submit" className="primary" disabled={saving} data-testid={`${testidScope}-save-button`}>
          {saving ? "Guardando…" : (editingExpense ? "Guardar cambios" : "Guardar egreso")}
        </button>
      </form>
    </div>
  );
}
